import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/site/Reveal";
import { scrollToId } from "@/lib/site";

const inputCls =
  "w-full flex-1 rounded-full border border-[#383029] bg-transparent px-6 py-4 text-sm text-[#FAF7F2] placeholder-[#A89F91] outline-none transition-colors duration-300 focus:border-[#E05A2C]";

const EASE = [0.16, 1, 0.3, 1];

function RadarMap({ places, center }) {
  const R = 168;
  const C = 200;
  const cosLat = Math.cos((center.lat * Math.PI) / 180);
  const toXY = (p) => {
    const dy = (p.lat - center.lat) * 110.57;
    const dx = (p.lng - center.lng) * 111.32 * cosLat;
    const dist = Math.min(Math.hypot(dx, dy), 3);
    const angle = Math.atan2(dy, dx);
    return [C + Math.cos(angle) * (dist / 3) * R, C - Math.sin(angle) * (dist / 3) * R];
  };
  return (
    <div data-testid="competition-radar" className="flex flex-col border border-[#2A241F] bg-[#16110E] p-6 sm:p-8">
      <svg viewBox="0 0 400 400" className="w-full" role="img" aria-label="Mappa radar delle strutture concorrenti">
        {[1, 2, 3].map((km) => (
          <g key={km}>
            <circle cx={C} cy={C} r={(km / 3) * R} fill="none" stroke="#2A241F" strokeWidth="1" />
            <text x={C + 6} y={C - (km / 3) * R + 14} fill="#A89F91" fontSize="10" fontFamily="JetBrains Mono, monospace">
              {km}km
            </text>
          </g>
        ))}
        <line x1={C - R} y1={C} x2={C + R} y2={C} stroke="#2A241F" strokeWidth="1" strokeDasharray="3 5" />
        <line x1={C} y1={C - R} x2={C} y2={C + R} stroke="#2A241F" strokeWidth="1" strokeDasharray="3 5" />
        {places.map((p, i) => {
          const [x, y] = toXY(p);
          return (
            <motion.circle
              key={`${p.lat}-${p.lng}-${i}`}
              cx={x}
              cy={y}
              r="5"
              fill="#E05A2C"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.85, scale: 1 }}
              transition={{ delay: 0.4 + i * 0.03, duration: 0.4, ease: EASE }}
              style={{ transformOrigin: `${x}px ${y}px` }}
            >
              <title>{p.name}</title>
            </motion.circle>
          );
        })}
        <motion.circle
          cx={C}
          cy={C}
          r="10"
          fill="none"
          stroke="#E05A2C"
          strokeWidth="1.5"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0.3, 1] }}
          transition={{ delay: 0.2, duration: 1.4 }}
        />
        <circle cx={C} cy={C} r="6" fill="#FAF7F2" />
        <text x={C} y={C - 16} textAnchor="middle" fill="#FAF7F2" fontSize="11" fontWeight="700" fontFamily="Archivo, sans-serif" letterSpacing="2">
          TU
        </text>
      </svg>
      <p className="mt-4 text-center font-mono text-xs uppercase tracking-widest text-[#A89F91] sm:text-xs">
        Ogni punto è una struttura registrata su Google entro 3km — passa sopra col mouse per il nome
      </p>
    </div>
  );
}

function IcebergBlock() {
  return (
    <div data-testid="iceberg-block" className="mt-10 max-w-4xl border border-[#4D2C20] bg-[#2B1B15] p-6 sm:p-10">
      <p className="section-kicker">Solo la punta dell'iceberg</p>
      <p className="mt-4 font-heading text-xl font-black uppercase leading-tight tracking-tight text-[#FAF7F2] sm:text-2xl">
        Questo numero conta gli hotel. Non i tuoi veri concorrenti.
      </p>
      <p className="mt-4 max-w-2xl text-base font-light leading-relaxed text-[#C4BBB0] sm:text-lg">
        Se vuoi sapere esattamente quante case vacanza e B&amp;B competono davvero con te su Airbnb e Booking, è parte di quello che analizzo nel Check-up del tuo annuncio — verificato a mano, annuncio per annuncio.
      </p>
      <button
        data-testid="iceberg-cta"
        onClick={() => scrollToId("hero-section")}
        className="btn-glow mt-8 rounded-full bg-[#E05A2C] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98]"
      >
        Fai valutare la tua struttura
      </button>
    </div>
  );
}

export default function CompetitionMap() {
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const zona = new URLSearchParams(window.location.search).get("zona");
    if (zona && zona.trim().length >= 2) {
      setLocation(zona.trim());
      submit(zona.trim(), true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const share = async () => {
    if (!result) return;
    const zona = result.label || location.trim();
    const url = `${window.location.origin}/?zona=${encodeURIComponent(zona)}#concorrenza`;
    const text = `A ${zona} risultano già ${result.approximate ? `più di ${result.count}` : result.count} strutture ricettive registrate su Google. Quanta concorrenza hai tu?`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "YU YieldUp — Quanta concorrenza hai?", text, url });
      } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Testo e link copiati — incollali dove vuoi.");
      } catch {
        toast.error("Copia non riuscita. Riprova.");
      }
    }
  };

  const submit = async (override, fromShare = false) => {
    const query = (typeof override === "string" ? override : location).trim();
    if (query.length < 2) {
      setNotice({ title: "Dimmi la zona", text: "Scrivi il paese o la zona della tua struttura, ad esempio «Noto, SR»." });
      setResult(null);
      return;
    }
    setLoading(true);
    setNotice(null);
    setResult(null);
    if (fromShare) setTimeout(() => scrollToId("concorrenza"), 600);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/competition-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ location: query }),
      });
      if (res.status === 429) {
        setNotice({ title: "Hai già fatto diverse prove", text: "Il conteggio gratuito è limitato. Quello dettagliato — i tuoi veri concorrenti su Booking e Airbnb — lo faccio io nel Check-up." });
      } else if (res.status === 503) {
        setNotice({ title: "Il conteggio automatico arriva a breve", text: "Stiamo attivando lo strumento. Intanto la tua zona la guardiamo insieme, a mano: lascia il link del tuo annuncio." });
      } else if (res.status === 404) {
        setNotice({ title: "Zona non trovata", text: "Prova con il nome del paese o della città più vicina, magari con la provincia (es. «Noto, SR»)." });
      } else if (res.ok) {
        setResult(await res.json());
        if (fromShare) setTimeout(() => scrollToId("concorrenza"), 300);
      } else {
        throw new Error();
      }
    } catch {
      setNotice({ title: "Qualcosa non ha funzionato", text: "Riprova tra poco — oppure passa direttamente alla valutazione gratuita." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="concorrenza" data-testid="competition-section" className="border-b border-[#2A241F] py-12 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="section-kicker">Quanta concorrenza hai</p>
          <h2 className="mt-4 max-w-4xl font-heading text-3xl font-black uppercase leading-[1.05] tracking-tight text-[#FAF7F2] sm:text-4xl lg:text-5xl">
            Ogni giorno qualcuno prenota loro, invece di te.
          </h2>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-[#C4BBB0]">
            Scrivi l'indirizzo della tua struttura — più è preciso, più il conteggio è centrato su di te. Poi ti spiego perché il numero vero è più alto.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div data-testid="competition-form" className="mt-10 flex max-w-2xl flex-col gap-3 sm:flex-row">
            <input
              data-testid="competition-location-input"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="Indirizzo o paese — es. Via Roma 12, Noto"
              className={inputCls}
            />
            <button
              data-testid="competition-submit-button"
              onClick={submit}
              disabled={loading}
              className="btn-glow shrink-0 rounded-full bg-[#E05A2C] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? "Conto in corso…" : "Scoprilo"}
            </button>
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          {result && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12"
            >
              <div className="grid gap-8 lg:grid-cols-2 lg:items-stretch">
                <RadarMap places={result.places || []} center={{ lat: result.lat, lng: result.lng }} />
                <div className="flex flex-col justify-center border border-[#2A241F] p-8 sm:p-10">
                  <p data-testid="competition-count" className="font-heading text-5xl font-black tracking-tighter text-[#E05A2C] sm:text-7xl">
                    {result.approximate ? `Oltre ${result.count}` : result.count}
                  </p>
                  <p className="mt-3 font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2] sm:text-xl">
                    strutture ricettive registrate{result.label ? ` — ${result.label}` : ""}
                  </p>
                  <p className="mt-5 max-w-md text-sm leading-relaxed text-[#C4BBB0] sm:text-base">
                    In quest'area risultano già {result.approximate ? `più di ${result.count}` : result.count} strutture ricettive registrate su Google. E questo conta solo hotel e B&amp;B con una scheda propria — non include la maggior parte dei singoli annunci Airbnb, che restano invisibili a questo tipo di conteggio.
                  </p>
                  <p className="mt-5 font-mono text-xs uppercase tracking-widest text-[#A89F91] sm:text-xs">
                    Strutture ricettive registrate nella zona, fonte Google
                  </p>
                  <button
                    data-testid="competition-share-button"
                    onClick={share}
                    className="mt-7 flex w-fit items-center gap-2.5 rounded-full border border-[#383029] px-6 py-3 text-xs font-medium text-[#FAF7F2] transition-colors duration-300 hover:border-[#E05A2C] hover:text-[#E05A2C] active:scale-[0.98] sm:text-sm"
                  >
                    <Share2 className="h-4 w-4" />
                    Condividi questo numero
                  </button>
                </div>
              </div>
              <IcebergBlock />
            </motion.div>
          )}

          {notice && !result && (
            <motion.div
              key="notice"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12"
            >
              <div data-testid="competition-notice" className="max-w-4xl border border-[#383029] p-6 sm:p-8">
                <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">{notice.title}</p>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#C4BBB0] sm:text-base">{notice.text}</p>
              </div>
              <IcebergBlock />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
